import { Alert, Form, FormInstance, Input, InputNumber, Select, Switch, message } from 'antd';
import { seasonService } from 'content/service';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SeasonListItem } from 'type/seasonType';
import {
  FASTVIEW_FEED_FORMAT,
  VIDEO_SOURCE_FIELD_METADATA_FALLBACK,
} from '../../../constants/feedFormat';
import { videoSourceService } from '../../../service/videoSourceService';
import { VideoSourceFieldMetadata } from '../../../type/videoSourceType';

const SEASON_PAGE_SIZE = '50';
const SEARCH_DEBOUNCE_MS = 300;

const DEFAULT_SEASON_HELP =
  'All content from this source is attached to this season, and the series/season values in the ' +
  'feed are ignored. Only seasons on the current platform are searchable.';

const DEFAULT_LANGUAGE_HELP =
  'Language applied to feed text (title, description, thumbnail, subtitle) when it has no lang ' +
  'attribute. Fastview feeds are Korean-only, so ko is recommended.';

const IMPORT_LIMIT_HELP =
  'Maximum items processed in a single import. Extras are dropped, keeping the newest first. ' +
  'Leave empty to use the server default (200).';

const BATCH_JOB_QUEUE_HELP =
  'AWS Batch job queue this source submits its imports to. Leave empty to use the server ' +
  'default queue.';

const AUTO_UPDATE_VIDEOS_HELP =
  'When off, already-ingested guids are not updated even if the vendor resends them. Keep this ' +
  'off for Fastview sources.';

const AUTO_UPDATE_VIDEOS_WARNING =
  'If it stays on, titles, thumbnails, and subtitles edited by operators are overwritten with ' +
  'feed values every time the vendor resends the same guid.';

interface VideoSourceFeedFormatFieldsProps {
  form: FormInstance;
}

/**
 * Fastview 숏폼 수집을 위한 피드 포맷 관련 필드.
 * 노출 필드/선택지는 서버 OPTIONS를 따르므로 백엔드 계보(main/staging) 차이를 흡수한다.
 */
const VideoSourceFeedFormatFields: React.FC<VideoSourceFeedFormatFieldsProps> = ({ form }) => {
  const [metadata, setMetadata] = useState<VideoSourceFieldMetadata>(
    VIDEO_SOURCE_FIELD_METADATA_FALLBACK
  );
  const [seasons, setSeasons] = useState<SeasonListItem[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<SeasonListItem | null>(null);
  const [loadingSeasons, setLoadingSeasons] = useState<boolean>(false);

  const feedFormat = Form.useWatch<string | undefined>('feed_format', form);
  const autoUpdateVideos = Form.useWatch<boolean | undefined>('auto_update_videos', form);
  const isFastview = feedFormat === FASTVIEW_FEED_FORMAT;

  // 시즌 목록은 백엔드가 현재 사용자 platform으로 필터링한다 (다른 플랫폼 시즌은 검색되지 않음)
  const loadSeasons = useCallback(async (search?: string): Promise<void> => {
    setLoadingSeasons(true);
    try {
      const params = new URLSearchParams();
      if (search) {
        params.set('search', search);
      }
      params.set('page_size', SEASON_PAGE_SIZE);
      const { results } = await seasonService.fetchSeasonList(params);
      setSeasons(results);
    } catch (error) {
      message.error((error as Error).message ?? 'Failed to load seasons');
    } finally {
      setLoadingSeasons(false);
    }
  }, []);

  const handleSearchSeason = useMemo(
    () => debounce((value: string) => loadSeasons(value), SEARCH_DEBOUNCE_MS),
    [loadSeasons]
  );

  useEffect(() => () => handleSearchSeason.cancel(), [handleSearchSeason]);

  useEffect(() => {
    videoSourceService.fetchFieldMetadata().then(setMetadata);
    loadSeasons();
  }, [loadSeasons]);

  // Edit 진입 시 이미 저장된 고정 시즌은 검색 결과 첫 페이지에 없을 수 있어 별도로 라벨을 확보한다
  useEffect(() => {
    const savedSeasonId = form.getFieldValue('default_season');
    if (!savedSeasonId) {
      return;
    }
    seasonService
      .fetchById(Number(savedSeasonId))
      .then(season => {
        if (season && season.id) {
          setSelectedSeason({
            id: season.id,
            title: season.title,
            slug: season.slug,
            status: '',
          } as SeasonListItem);
        }
      })
      .catch(() => undefined);
  }, [form]);

  const seasonOptions = useMemo(() => {
    const merged =
      selectedSeason && !seasons.some(season => season.id === selectedSeason.id)
        ? [selectedSeason, ...seasons]
        : seasons;
    return merged.map(season => ({
      value: season.id,
      label: season.slug ? `${season.title} (${season.slug})` : season.title,
    }));
  }, [seasons, selectedSeason]);

  const supports = (field: string): boolean => metadata.supportedFields.includes(field);

  return (
    <>
      {supports('feed_format') && (
        <Form.Item
          label="Feed Format"
          name="feed_format"
          extra="Select fastview for Fastview Shorts feeds. auto keeps the existing domain-based routing (Zype/ODK)."
        >
          <Select placeholder="auto" options={metadata.feedFormatOptions} />
        </Form.Item>
      )}

      {isFastview && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="Fastview source setup order"
          description={
            <>
              <ol style={{ paddingLeft: 20, marginBottom: 4 }}>
                <li>Create the Fastview Series, then its Season</li>
                <li>Create ServiceAutoDistribution (odk / amasian) on that season (or series)</li>
                <li>Set the season from step 2 as the Default Season below</li>
              </ol>
              Skipping step 2 still ingests content, but it will not be distributed to any service.
              Target Services below has no effect on the Fastview path — set distribution on the
              season/series ServiceAutoDistribution.
            </>
          }
        />
      )}

      {/* 고정 시즌·기본 언어는 fastview 전용 값이라 다른 포맷에서는 무시된다 */}
      {isFastview && supports('default_season') && (
        <Form.Item
          label="Default Season"
          name="default_season"
          rules={[{ required: true, message: 'Set the default season before the first import!' }]}
          extra={DEFAULT_SEASON_HELP}
        >
          <Select
            showSearch
            allowClear
            filterOption={false}
            loading={loadingSeasons}
            placeholder="Search seasons (title / slug)"
            onSearch={handleSearchSeason}
            onChange={(value?: number) => {
              // 지우면 undefined가 되어 PATCH에서 빠지므로, 해제 의도를 null로 남긴다
              form.setFieldValue('default_season', value ?? null);
              setSelectedSeason(seasons.find(season => season.id === value) ?? null);
            }}
            options={seasonOptions}
          />
        </Form.Item>
      )}

      {isFastview && supports('default_language') && (
        <Form.Item label="Default Language" name="default_language" extra={DEFAULT_LANGUAGE_HELP}>
          <Select placeholder="ko" options={metadata.defaultLanguageOptions} />
        </Form.Item>
      )}

      {supports('import_limit') && (
        <Form.Item label="Import Limit" name="import_limit" extra={IMPORT_LIMIT_HELP}>
          <InputNumber min={1} style={{ width: 200 }} />
        </Form.Item>
      )}

      {supports('batch_job_queue') && (
        <Form.Item label="Batch Job Queue" name="batch_job_queue" extra={BATCH_JOB_QUEUE_HELP}>
          <Input />
        </Form.Item>
      )}

      <Form.Item
        label="Auto Update Videos"
        name="auto_update_videos"
        valuePropName="checked"
        extra={AUTO_UPDATE_VIDEOS_HELP}
      >
        <Switch />
      </Form.Item>

      {isFastview && autoUpdateVideos && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          message="Turn off Auto Update Videos for Fastview sources"
          description={AUTO_UPDATE_VIDEOS_WARNING}
        />
      )}
    </>
  );
};

export default VideoSourceFeedFormatFields;
